import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TrackingService } from "../services/trackingService";
import { CareUnit, Reason } from "../types";

export interface TrackingState {
  hashedEmail: string;
  setHashedEmail: (email: string) => void;
  petSpeciesName: string;
  setPetSpeciesName: (name: string) => void;
  bookingId: number | undefined;
  setBookingId: (id: number | undefined) => void;
  selectedCareUnit: CareUnit | undefined;
  /** Sets (or clears) the tracked clinic, and invalidates any care unit lookup in flight. */
  setSelectedCareUnit: (careUnit: CareUnit | undefined) => void;
  /**
   * Clears the tracked clinic, then sets the one the lookup finds, unless the clinic has
   * changed (or been cleared) in the meantime.
   */
  loadSelectedCareUnit: (
    lookup: () => Promise<CareUnit | null | undefined>
  ) => Promise<void>;
  trackingService: TrackingService;
}

interface UseProvetTrackingOptions {
  advancedTrackingEnabled: boolean;
  sessionId: string;
  selectedReason?: Reason;
}

/**
 * Custom hook to manage tracking state and tracking service for the Provet booking form.
 * Handles analytics tracking, user identification, and booking metrics.
 */
export const useProvetTracking = ({
  advancedTrackingEnabled,
  sessionId,
  selectedReason
}: UseProvetTrackingOptions): TrackingState => {
  // Tracking state
  const [hashedEmail, setHashedEmail] = useState<string>("");
  const [petSpeciesName, setPetSpeciesName] = useState<string>("");
  const [bookingId, setBookingIdState] = useState<number | undefined>();
  const [selectedCareUnit, setSelectedCareUnitState] = useState<CareUnit | undefined>();

  // The latest booking id and clinic, so a rebuilt tracking service starts with them
  const bookingIdRef = useRef<number | undefined>(undefined);
  const selectedCareUnitRef = useRef<CareUnit | undefined>(undefined);
  // Counts the clinic changes, so a care unit lookup that was overtaken (by another clinic
  // choice, or by clearing the clinic, e.g. on "book another") is ignored when it resolves
  const careUnitVersionRef = useRef(0);

  // Initialize tracking service
  const trackingService = useMemo(
    () =>
      new TrackingService({
        advancedTrackingEnabled,
        sessionId,
        selectedCareUnit: selectedCareUnitRef.current,
        selectedReason,
        petSpeciesName,
        hashedEmail,
        bookingId: bookingIdRef.current
      }),
    [advancedTrackingEnabled, sessionId]
  );

  // The booking id and clinic are set on the tracking service straight away, so the events
  // tracked right after setting them (e.g. Confirmed) carry them without depending on when
  // React runs effects.
  const setBookingId = useCallback(
    (id: number | undefined) => {
      bookingIdRef.current = id;
      trackingService.updateOptions({ bookingId: id });
      setBookingIdState(id);
    },
    [trackingService]
  );

  const applySelectedCareUnit = useCallback(
    (careUnit: CareUnit | undefined) => {
      selectedCareUnitRef.current = careUnit;
      trackingService.updateOptions({ selectedCareUnit: careUnit });
      setSelectedCareUnitState(careUnit);
    },
    [trackingService]
  );

  const setSelectedCareUnit = useCallback(
    (careUnit: CareUnit | undefined) => {
      careUnitVersionRef.current++;
      applySelectedCareUnit(careUnit);
    },
    [applySelectedCareUnit]
  );

  const loadSelectedCareUnit = useCallback(
    async (lookup: () => Promise<CareUnit | null | undefined>) => {
      // Clear the previous clinic first, so events never carry it if the lookup fails
      setSelectedCareUnit(undefined);
      const version = careUnitVersionRef.current;

      try {
        const careUnit = await lookup();

        if (careUnit && version === careUnitVersionRef.current) {
          applySelectedCareUnit(careUnit);
        }
      } catch (error) {
        console.error("Error fetching care unit:", error);
      }
    },
    [setSelectedCareUnit, applySelectedCareUnit]
  );

  // Update tracking service when the other tracking data changes
  useEffect(() => {
    trackingService.updateOptions({
      selectedReason,
      petSpeciesName,
      hashedEmail
    });
  }, [selectedReason, petSpeciesName, hashedEmail, trackingService]);

  return {
    hashedEmail,
    setHashedEmail,
    petSpeciesName,
    setPetSpeciesName,
    bookingId,
    setBookingId,
    selectedCareUnit,
    setSelectedCareUnit,
    loadSelectedCareUnit,
    trackingService
  };
};
